import type { TableSelectionData } from '../useAutoContextSelection'
import type { GenerativeComponentData } from '../generativeComponents'

export type ContextDataLite = {
  currency?: string
  tableSelection?: TableSelectionData | null
  revenueWaterfallData?: any
}

export type CannedResult = {
  prose: string
  components?: GenerativeComponentData[]
}

const getFocusedCustomer = (
  contextData?: ContextDataLite
): { id?: string; name?: string } => {
  if (!contextData) return {}
  const tableSelection = contextData.tableSelection
  if (tableSelection) {
    const foundCustomer = tableSelection.cellData.find(
      c => c.rowData?.type === 'customer' && c.rowData.id
    )

    if (foundCustomer) {
      return {
        id: foundCustomer.rowData.id,
        name: foundCustomer.rowData.label
      }
    }
  }
  return {}
}

function seatUpsellCanned(
  userMessage: string,
  contextData?: ContextDataLite
): CannedResult {
  const focus = getFocusedCustomer(contextData)
  const customerName = focus.name || 'Customer'

  const monthMatch = userMessage.match(
    /\b(january|february|march|april|may|june|july|august|september|october|november|december)\b/i
  )
  const monthPretty = monthMatch
    ? `${monthMatch[0][0].toUpperCase()}${monthMatch[0].slice(1).toLowerCase()}`
    : 'this month'
  const seatsMatch = userMessage.match(/(\d{1,3})\s+seat/i)
  const seatsAdded = seatsMatch ? parseInt(seatsMatch[1], 10) : 4
  const currencyCode = contextData?.currency || 'USD'
  const currencySymbol =
    currencyCode === 'EUR' ? '€' : currencyCode === 'GBP' ? '£' : '$'
  const seatPrice = 49.0
  const addedDeferred = seatsAdded * seatPrice

  const prose = `**Seat Upsell Analysis (${monthPretty})**\n\n- Customer: ${customerName}\n- Seat product price: ${currencySymbol}${seatPrice.toFixed(2)} per seat\n- Seats added: ${seatsAdded}\n- Deferred revenue added: ${currencySymbol}${addedDeferred.toFixed(2)}\n\nUnder ASC 606, the incremental consideration for the additional seats is initially deferred and recognized over the remaining performance period (same recognition method as the base seat product, typically Straight line). The deferral schedule will ramp by ${currencySymbol}${seatPrice.toFixed(2)} per seat per period until fully recognized.`

  const components: GenerativeComponentData[] = [
    {
      // minimal pill
      type: 'customer-pill',
      name: customerName,
      customerId: focus.id
    } as any,
    {
      type: 'metric',
      title: 'Deferred revenue added',
      value: `${currencySymbol}${addedDeferred.toFixed(2)}`
    } as any
  ]

  return { prose, components }
}

// Utility formatting helpers
const currencySymbol = (code?: string) =>
  code === 'EUR' ? '€' : code === 'GBP' ? '£' : '$'
const fmt = (n: number, code?: string) => {
  const sym = currencySymbol(code)
  const parts = n.toFixed(2).split('.')
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return `${sym}${parts.join('.')}`
}

const parseMonthKeyToISO = (display: string): string => {
  const m = display.match(/^(\w{3})\s+(\d{4})$/)
  if (!m) return new Date().toISOString().slice(0, 10)
  const monthMap: Record<string, number> = {
    Jan: 0,
    Feb: 1,
    Mar: 2,
    Apr: 3,
    May: 4,
    Jun: 5,
    Jul: 6,
    Aug: 7,
    Sep: 8,
    Oct: 9,
    Nov: 10,
    Dec: 11
  }
  const month = monthMap[m[1]] ?? 0
  const year = parseInt(m[2], 10)
  const d = new Date(year, month, 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`
}

function selectFocusRow(contextData?: ContextDataLite) {
  const rows: any[] = contextData?.revenueWaterfallData?.data?.flatRows || []
  if (!rows || rows.length === 0) return null
  const sel = contextData?.tableSelection
  if (sel && sel.cellData && sel.cellData.length > 0) {
    const first = sel.cellData[0]
    // Prefer direct rowData if present
    if (first.rowData) {
      const label = first.rowData.label
      const id = first.rowData.id || first.rowData.customerId
      const match = rows.find(
        r => r.id === id || r.customerId === id || r.label === label
      )
      if (match) return match
    }
    // Fallback by row index
    if (typeof first.row === 'number' && rows[first.row]) {
      return rows[first.row]
    }
  }
  // Otherwise pick the top-level highest recognized row
  return rows
    .filter(r => r.depth === 0)
    .sort((a, b) => (b.recognized ?? 0) - (a.recognized ?? 0))[0]
}

function breakdownFromWaterfall(contextData?: ContextDataLite): CannedResult {
  const data = contextData?.revenueWaterfallData?.data
  const row = selectFocusRow(contextData)
  const code = contextData?.currency || 'USD'

  if (!data || !row) {
    // Fallback generic placeholder
    return {
      prose:
        '**Breakdown Preview**\n\nNo specific selection found. Showing placeholder breakdown until data is available.',
      components: [
        {
          type: 'insight',
          title: 'Breakdown (placeholder)',
          message: 'Detailed breakdown UI is coming soon.'
        } as any
      ]
    }
  }

  // Summary metrics from selected row (demo: using booked/recognized/remaining from row)
  const booked = Number(row.bookedTotal ?? 0)
  const recognized = Number(row.recognized ?? 0)
  const remaining = Number(row.remaining ?? 0)
  const name = row.label || 'Selected'

  // Build monthly schedule from row.monthlyValues as a proxy for recognized schedule in demo
  const months: string[] = data.monthlyColumns || []
  const rowsTable: Array<[string, string]> = []
  const chartData: Array<{ date: string; value: number }> = []
  months.forEach(m => {
    const v = Number(row.monthlyValues?.[m] ?? 0)
    if (v > 0) {
      rowsTable.push([m, fmt(v, code)])
      chartData.push({ date: parseMonthKeyToISO(m), value: v })
    }
  })

  // Canned recommendations based on remaining and distribution
  let recs: string[]
  if (remaining <= 0.001) {
    recs = [
      `Initiate renewal conversations ahead of schedule completion.`,
      `Review product usage patterns to discover expansion opportunities.`,
      `Perform a quick health check to maximize retention potential.`
    ]
  } else if (rowsTable.length >= 6) {
    recs = [
      `Monitor recognition pace; consider catch‑up entries if schedules slip.`,
      `Evaluate billing cadence to align cash and revenue timing.`,
      `Flag long‑tail remaining (${fmt(remaining, code)}) for RevOps follow‑up.`
    ]
  } else {
    recs = [
      `Validate schedule allocation for upcoming periods.`,
      `Confirm invoicing matches recognition milestones.`,
      `Track customer engagement around key delivery dates.`
    ]
  }

  const prose = `Certainly, here's a concise breakdown for \`${name}\` with a demo schedule.\n\n**Key Takeaways**\n\n- Total booked: **${fmt(booked, code)}**\n- Recognized to date: **${fmt(recognized, code)}**\n- Remaining: **${fmt(remaining, code)}**\n\n**Recommendations / Next Steps**\n\n${recs.map((r, i) => `${i + 1}. ${r}`).join('\n')}`

  const components: GenerativeComponentData[] = [
    {
      type: 'kpi-dashboard',
      title: `Revenue Summary for ${name}`,
      metrics: [
        { label: 'Total Booked Revenue', value: fmt(booked, code) },
        { label: 'Total Recognized Revenue', value: fmt(recognized, code) },
        { label: 'Remaining Revenue', value: fmt(remaining, code) }
      ]
    } as any,
    {
      type: 'table',
      title: `Monthly Revenue Recognition Schedule for ${name}`,
      headers: ['Month', 'Recognized Amount'],
      rows: rowsTable
    } as any,
    {
      type: 'chart',
      title: `${name}: Monthly Recognized Revenue`,
      data: chartData,
      unit: currencySymbol(code),
      variant: 'bar',
      dateFrom: chartData.length ? chartData[0].date : undefined,
      dateTo: chartData.length
        ? chartData[chartData.length - 1].date
        : undefined,
      unitTickValues: [
        0,
        Math.max(1, Math.round(booked / 6 / 100) * 100),
        Math.max(1, Math.round(booked / 3 / 100) * 100)
      ]
    } as any
  ]

  return { prose, components }
}

export function detectCannedResponse(
  userMessage: string,
  contextData?: ContextDataLite
): CannedResult | null {
  const msg = userMessage.toLowerCase()
  if (msg.includes('seat') || msg.includes('seats') || msg.includes('upsell')) {
    return seatUpsellCanned(userMessage, contextData)
  }
  if (msg.includes('breakdown')) {
    return breakdownFromWaterfall(contextData)
  }
  return null
}
